import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "../hooks/useSEO";
import { contentAPI } from "../lib/api";
import { AdSlot } from "../components/AdSlot";
import { ArrowRight, Clock } from "lucide-react";

const BlogPage = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    contentAPI.blogList().then(setPosts).catch(() => setPosts([]));
  }, []);

  useSEO({
    title: "OBBBA Editorial — 2026 Tax Changes Explained",
    description:
      "In-depth editorial briefings on the One Big Beautiful Bill Act: new deductions, permanent TCJA provisions, and 2026 planning strategies.",
    keywords: "obbba blog, 2026 tax changes, obbba provisions, tax planning 2026, public law 119-21",
    path: "/blog",
  });

  const [hero, ...rest] = posts;

  return (
    <>
      <section className="border-b-2 border-foreground py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-4 block">
            The Editorial Desk
          </span>
          <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-6" data-testid="blog-title">
            Briefings on the <span className="italic">2026 OBBBA era</span>.
          </h1>
          <p className="font-body text-lg text-foreground/80 max-w-3xl leading-relaxed">
            Long-form editorial analysis of the One Big Beautiful Bill Act and its downstream
            effects on households, small businesses, and estates.
          </p>
        </div>
      </section>

      {hero && (
        <section className="py-12 md:py-16 border-b-2 border-foreground">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to={`/blog/${hero.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-5 gap-10 items-start"
              data-testid={`blog-hero-${hero.slug}`}
            >
              <div className="lg:col-span-2 aspect-[4/5] bg-foreground relative overflow-hidden border-2 border-foreground">
                <img
                  src="https://images.unsplash.com/photo-1561518658-db43bb106aeb?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                  alt=""
                  className="w-full h-full object-cover grayscale mix-blend-luminosity opacity-70 group-hover:opacity-90 transition-opacity"
                />
                <div className="absolute top-4 left-4 bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-widest px-2 py-1">
                  Featured
                </div>
              </div>
              <div className="lg:col-span-3">
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary">{hero.category}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {hero.read_minutes} min read
                  </span>
                </div>
                <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-5 leading-[1.05] group-hover:text-primary transition-colors">
                  {hero.title}
                </h2>
                <p className="font-body text-lg text-foreground/80 leading-relaxed mb-6">{hero.excerpt}</p>
                <div className="font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
                  Read briefing <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      <AdSlot size="leaderboard" testid="blog-mid-ad" />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-semibold mb-10 border-b-2 border-foreground pb-4">All briefings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group border-b border-foreground/20 pb-8 hover:border-foreground transition-colors"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-primary">{p.category}</span>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{p.read_minutes} min</span>
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-3 leading-snug group-hover:text-primary transition-colors">
                  {p.title}
                </h3>
                <p className="font-body text-sm text-foreground/80 leading-relaxed mb-4">{p.excerpt}</p>
                <span className="font-mono uppercase text-[10px] tracking-widest text-muted-foreground">
                  {new Date(p.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPage;
